// The one resource registry of both graphics backends (R1369).
//
// A lost context or a lost device takes every GPU object with it. The
// registry is the list that brings them back: the loss destroys every
// registered resource, and the restore creates each one again in the order
// it was registered. Registration order is dependency order, so a batch
// registered after its program or its corner buffer finds that object
// already rebuilt.
//
// The registry is generic over the backend HANDLE: the `WebGL2RenderingContext`
// on WebGL2, the `GPUDevice` on WebGPU. A resource takes the handle in both
// calls. A WebGPU resource declares `destroy()` with no argument and ignores
// the device, because a WebGPU object destroys itself; TypeScript accepts a
// method with fewer parameters, so one shape covers both backends.
//
// The context keeps `lost` and the handle, and the registry reads both
// through two callbacks. Nothing here runs in a frame: a context calls the
// registry when an object is made or disposed, on a loss and on a restore.

/**
 * One GPU object a context owns.
 *
 * `create` builds the object on the live handle; `destroy` drops the object.
 * Both must be safe to call twice.
 */
export interface GfxResource<H> {
  create(handle: H): void;
  destroy(handle: H): void;
}

/** What the registry reads from the context that owns it. */
export interface RegistryHost<H> {
  /** The handle a resource builds on now. WebGPU replaces it on a restore. */
  handle(): H;
  /** True between a loss and the restore that ends it. */
  lost(): boolean;
  /**
   * True where `unregister` and `dispose` destroy a resource while the
   * context is lost. WebGPU sets it: a WebGPU object destroys itself, and an
   * object built outside `create` may still exist. WebGL2 clears it: a lost
   * context's handles are gone, and a delete call on them does nothing.
   */
  readonly destroyWhileLost: boolean;
}

/** The ordered list of resources and restore callbacks of one context. */
export class ResourceRegistry<H> {
  private resources: GfxResource<H>[] = [];
  private readonly restoreFns: Array<() => void> = [];

  constructor(private readonly host: RegistryHost<H>) {}

  /** Adds a resource at the end of the order, and builds it on a live context. */
  register(r: GfxResource<H>): void {
    this.resources.push(r);
    if (!this.host.lost()) r.create(this.host.handle());
  }

  /** Takes a resource out of the order, and destroys it. */
  unregister(r: GfxResource<H>): void {
    const i = this.resources.indexOf(r);
    if (i >= 0) this.resources.splice(i, 1);
    if (this.host.destroyWhileLost || !this.host.lost()) r.destroy(this.host.handle());
  }

  /** Adds a callback that runs after every restore, after the resources. */
  onRestore(fn: () => void): void { this.restoreFns.push(fn); }

  /**
   * Destroys every resource in registration order, on a loss.
   *
   * An error of one resource does not stop the others: its handles are
   * already gone with the context.
   */
  destroyAll(): void {
    const h = this.host.handle();
    for (const r of this.resources) {
      try { r.destroy(h); } catch { /* the handles are already gone */ }
    }
  }

  /**
   * Builds every resource again in registration order, then runs every
   * restore callback. The context clears `lost` before it calls this.
   */
  restore(): void {
    const h = this.host.handle();
    for (const r of this.resources) r.create(h);
    for (const fn of this.restoreFns) fn();
  }

  /** Destroys every resource (on a live context, or always under `destroyWhileLost`) and empties the list. */
  dispose(): void {
    if (this.host.destroyWhileLost || !this.host.lost()) this.destroyAll();
    this.resources = [];
  }
}
